import { HEALTHCONTOURS_STRIPE_METADATA_ID } from "config/healthContoursPricing";
import { db } from "./firebase";

/**
 * Fetches all active prices across all active plans for a given role.
 * Returns a flat list so callers can shape the result as they need.
 */
export const fetchActivePricesForRole = async (role) => {
  if (!role || !db?.collection) return [];

  const plansSnapshot = await db
    .collection("plans")
    .where("active", "==", true)
    .where("role", "==", role)
    .get();

  const results = [];
  for (const planDoc of plansSnapshot.docs) {
    const pricesSnapshot = await db
      .collection("plans")
      .doc(planDoc.id)
      .collection("prices")
      .where("active", "==", true)
      .get();

    pricesSnapshot.forEach((priceDoc) => {
      results.push({
        priceId: priceDoc.id,
        price: priceDoc.data(),
        product: planDoc.data(),
      });
    });
  }
  return results;
};

/**
 * Loads admin seat pricing keyed by billing interval.
 * Shape: { monthly: { id, amount, interval } | null, yearly: ... | null }
 */
export const loadSeatPricing = async () => {
  const entries = await fetchActivePricesForRole("admin");
  const result = { monthly: null, yearly: null };
  for (const { priceId, price } of entries) {
    const key = price.interval === "month" ? "monthly" : "yearly";
    result[key] = {
      id: priceId,
      amount: price.unit_amount / 100,
      interval: key,
    };
  }
  return result;
};

/**
 * Active Stripe prices tagged for HealthContours
 * (`plans.stripe_metadata_id === "healthcontours"`).
 */
export const fetchActiveHealthContoursPrices = async () => {
  if (!db?.collection) return [];

  const plansSnapshot = await db
    .collection("plans")
    .where("active", "==", true)
    .get();

  const results = [];
  for (const planDoc of plansSnapshot.docs) {
    const product = planDoc.data() || {};
    if (!isHealthContoursPlan(product)) continue;
    const pricesSnapshot = await db
      .collection("plans")
      .doc(planDoc.id)
      .collection("prices")
      .where("active", "==", true)
      .get();

    pricesSnapshot.forEach((priceDoc) => {
      results.push({
        priceId: priceDoc.id,
        price: priceDoc.data(),
        product,
      });
    });
  }
  return results;
};

function isHealthContoursPlan(product) {
  const metaId =
    product?.stripe_metadata_id ||
    product?.metadata?.id ||
    product?.metadata?.stripe_metadata_id ||
    null;
  const tier =
    product?.healthcontours_tier ||
    product?.metadata?.healthcontours_tier ||
    null;
  if (metaId === HEALTHCONTOURS_STRIPE_METADATA_ID) return true;
  if (typeof tier === "string" && tier.startsWith("healthcontours_")) return true;
  return false;
}
